import api from '../../../lib/api';

export type BlumeIntegrationStatus = 'ACTIVE' | 'DISCONNECTED' | 'ERROR';

export type BlumeMappingStatus = 'UNMAPPED' | 'AUTO_MATCHED' | 'MANUAL' | 'IGNORED';

/*
 * ============================================================
 * CUSTOMER INTEGRATION
 * ============================================================
 */

export type BlumeCounts = {
  mapped: number;
  autoMatched: number;
  manual: number;
  needsReview: number;
  blumeOnly: number;
  ignored: number;
  stale: number;
  bmpOnly: number;
};

export type BlumeOverview = {
  connected: boolean;

  status: BlumeIntegrationStatus | null;

  connectedAt: string | null;

  lastSyncAt: string | null;

  lastSyncError: string | null;

  counts: BlumeCounts;
};

export type BmpCustomerSummary = {
  id: string;

  name: string | null;

  email: string;

  status: string;

  archivedAt?: string | null;

  blumeCreditAvailable?: number | null;
  blumeCreditLimit?: number | null;

  blumeOverdueInvoiceBalance?: number | null;

  blumeCreditUpdatedAt?: string | null;

  blumeCreditStaleAt?: string | null;
};

export type BlumeMappedCustomerSummary = BmpCustomerSummary & {
  mappingId: string;

  mappedAt: string;

  mappingUpdatedAt: string;
};

export type BlumeMapping = {
  id: string;

  blumeCustomerId: string;

  blumeName: string;

  normalizedName: string;

  creditAvailable: number;
  creditLimit: number | null;

  overdueInvoiceBalance: number | null;

  mappingStatus: BlumeMappingStatus;

  matchScore: number | null;

  isPresentInBlume: boolean;

  lastSeenAt: string;

  createdAt: string;

  updatedAt: string;

  /**
   * One Blume customer can feed multiple BMP customers.
   */
  bmpCustomers: BlumeMappedCustomerSummary[];

  suggestedBmpCustomer: BmpCustomerSummary | null;
};

export type BlumeMappingsResponse = {
  integration: {
    id: string;

    status: BlumeIntegrationStatus;

    connectedAt: string;

    lastSyncAt: string | null;

    lastSyncError: string | null;
  };

  counts: BlumeCounts;

  mappings: BlumeMapping[];

  /**
   * Active BMP customers that currently have no Blume mapping.
   * These are the candidates shown by the add-mapping modal.
   */
  bmpOnlyCustomers: BmpCustomerSummary[];
};

export type BlumeSyncResult = {
  received: number;

  mapped: number;

  autoMatched: number;

  unmapped: number;

  ignored: number;

  stale: number;

  bmpOnly: number;
};

export type ConnectBlumeResponse = {
  authorizeUrl: string;
};

export type BlumeSyncResponse = {
  success: boolean;

  sync: BlumeSyncResult;
};

export type BlumeMappingMutationResponse = {
  success: boolean;

  mapping: BlumeMapping;
};

/**
 * Main Blume integration overview.
 */
export async function getBlumeOverview(): Promise<BlumeOverview> {
  const { data } = await api.get<BlumeOverview>('/integrations/blume');

  return data;
}

/**
 * Full Blume ↔ BMP customer mapping dataset.
 */
export async function getBlumeMappings(): Promise<BlumeMappingsResponse> {
  const { data } = await api.get<BlumeMappingsResponse>('/integrations/blume/mappings');

  return data;
}

/**
 * Start OAuth / PKCE connection flow.
 *
 * The caller should redirect the browser to authorizeUrl.
 */
export async function connectBlume(): Promise<ConnectBlumeResponse> {
  const { data } = await api.post<ConnectBlumeResponse>('/integrations/blume/connect');

  return data;
}

/**
 * Manually trigger a Blume customer sync.
 */
export async function syncBlumeCustomers(): Promise<BlumeSyncResponse> {
  const { data } = await api.post<BlumeSyncResponse>('/integrations/blume/sync');

  return data;
}

/**
 * Add a BMP customer mapping to a Blume customer.
 *
 * Existing BMP mappings for this Blume customer remain intact.
 * This also serves as "Accept suggestion".
 */
export async function mapBlumeCustomer(
  blumeCustomerId: string,

  bmpCustomerId: string
): Promise<BlumeMappingMutationResponse> {
  const { data } = await api.post<BlumeMappingMutationResponse>(
    `/integrations/blume/mappings/${encodeURIComponent(blumeCustomerId)}/customers`,
    {
      bmpCustomerId,
    }
  );

  return data;
}

/**
 * Remove one specific BMP customer from a Blume customer's mappings.
 *
 * Any other BMP customers mapped to the same Blume customer remain mapped.
 */
export async function unmapBlumeCustomer(
  blumeCustomerId: string,

  bmpCustomerId: string
): Promise<BlumeMappingMutationResponse> {
  const { data } = await api.delete<BlumeMappingMutationResponse>(
    `/integrations/blume/mappings/${encodeURIComponent(
      blumeCustomerId
    )}/customers/${encodeURIComponent(bmpCustomerId)}`
  );

  return data;
}

/**
 * Explicitly ignore a Blume customer.
 *
 * The backend removes all of its existing BMP mappings before marking it ignored.
 */
export async function ignoreBlumeCustomer(
  blumeCustomerId: string
): Promise<BlumeMappingMutationResponse> {
  const { data } = await api.post<BlumeMappingMutationResponse>(
    `/integrations/blume/mappings/${encodeURIComponent(blumeCustomerId)}/ignore`
  );

  return data;
}

/**
 * Undo Ignore and return the customer to UNMAPPED.
 */
export async function unignoreBlumeCustomer(
  blumeCustomerId: string
): Promise<BlumeMappingMutationResponse> {
  const { data } = await api.delete<BlumeMappingMutationResponse>(
    `/integrations/blume/mappings/${encodeURIComponent(blumeCustomerId)}/ignore`
  );

  return data;
}

/*
 * ============================================================
 * PRODUCT / QUICKBOOKS ITEM INTEGRATION
 * ============================================================
 */

/**
 * Live QBO Item snapshot returned by Blume.
 *
 * quickBooksItemId is the real QuickBooks Item.Id.
 *
 * SKU comes from QuickBooks and is authoritative for a
 * mapped product.
 */
export type BlumeQuickBooksItem = {
  quickBooksItemId: string;

  name: string;

  fullyQualifiedName: string | null;

  description: string | null;

  sku: string | null;

  type: string;

  active: boolean;

  unitPrice: number | null;

  purchaseCost?: number | null;

  taxable: boolean | null;

  trackQtyOnHand?: boolean | null;

  qtyOnHand?: number | null;

  incomeAccountId?: string | null;

  incomeAccountName?: string | null;

  expenseAccountId?: string | null;

  expenseAccountName?: string | null;

  assetAccountId?: string | null;

  assetAccountName?: string | null;

  parentItemId?: string | null;

  parentItemName?: string | null;
};

/**
 * Persisted BMP Product -> QuickBooks Item mapping.
 *
 * We deliberately store the actual QBO Item.Id rather
 * than Blume's local QuickBooksItem table id.
 */
export type BlumeProductMapping = {
  id: string;

  organizationId: string;

  productId: string;

  quickBooksItemId: string;

  quickBooksItemName: string;

  quickBooksItemFullyQualifiedName: string | null;

  quickBooksItemType: string;

  createdAt: string;

  updatedAt: string;
};

/**
 * BMP Product as returned by the Blume product-mapping
 * dataset.
 *
 * `sku` is BMP's local copy of the QBO SKU.
 *
 * For existing mapped products, the backend backfills a
 * missing BMP SKU from the live QBO item.
 */
export type BlumeBmpProduct = {
  id: string;

  name: string;

  sku: string | null;

  description: string | null;

  deliveryType: 'BULK' | 'PALLET';

  imageUrl: string | null;

  createdAt: string;

  updatedAt: string;

  blumeMapping: BlumeProductMapping | null;

  /**
   * Live QBO item for the current mapping.
   *
   * null means:
   * - the product is not mapped, or
   * - its mapped QBO item is no longer available in
   *   the live Blume /items response.
   */
  mappedQuickBooksItem: BlumeQuickBooksItem | null;

  /**
   * null  = product has no mapping
   * true  = mapped QBO item exists in the live catalog
   * false = mapping exists but QBO item is unavailable
   */
  mappingAvailableInQuickBooks: boolean | null;
};

export type BlumeProductCounts = {
  total: number;

  mapped: number;

  unmapped: number;

  unavailable: number;
};

export type BlumeProductMappingsResponse = {
  integration: {
    id: string;

    status: BlumeIntegrationStatus;

    scope: string;

    connectedAt: string;
  };

  counts: BlumeProductCounts;

  products: BlumeBmpProduct[];

  quickBooksItems: BlumeQuickBooksItem[];
};

/**
 * Product summary returned by map/create/unmap mutations.
 *
 * SKU is included because mapping an existing QBO item
 * may update Product.sku from QuickBooks.
 */
export type BlumeProductMutationSummary = {
  id: string;

  name: string;

  sku: string | null;

  deliveryType: 'BULK' | 'PALLET';
};

export type MapExistingBlumeProductResponse = {
  success: boolean;

  product: BlumeProductMutationSummary;

  mapping: BlumeProductMapping;

  quickBooksItem: BlumeQuickBooksItem;
};

/**
 * Values the browser may supply while creating a NEW
 * QuickBooks item.
 *
 * SKU is intentionally absent.
 *
 * The backend reads Product.sku from BMP and sends it as
 * the proposed QBO SKU. Once QBO creates the item, the
 * returned QBO SKU becomes authoritative.
 */
export type CreateAndMapBlumeProductInput = {
  unitPrice?: number | null;

  taxable?: boolean | null;
};

export type CreateAndMapBlumeProductResponse = {
  success: boolean;

  product: BlumeProductMutationSummary;

  mapping: BlumeProductMapping;

  quickBooksItem: BlumeQuickBooksItem;
};

export type UnmapBlumeProductResponse = {
  success: boolean;

  product: BlumeProductMutationSummary;

  removedMapping: BlumeProductMapping;
};

/**
 * Full BMP ↔ QuickBooks product mapping dataset.
 *
 * Loading this endpoint may also cause the backend to
 * backfill Product.sku for older mappings where BMP has
 * no SKU but the mapped QBO item does.
 */
export async function getBlumeProductMappings(): Promise<BlumeProductMappingsResponse> {
  const { data } = await api.get<BlumeProductMappingsResponse>('/integrations/blume/products');

  return data;
}

/**
 * Map a BMP Product to an EXISTING QBO Item.
 *
 * QBO is authoritative for SKU.
 *
 * If the selected QBO item has an SKU, the backend
 * reconciles Product.sku to that value while saving the
 * mapping.
 */
export async function mapExistingBlumeProduct(
  productId: string,

  quickBooksItemId: string
): Promise<MapExistingBlumeProductResponse> {
  const { data } = await api.put<MapExistingBlumeProductResponse>(
    `/integrations/blume/products/${encodeURIComponent(productId)}/mapping`,
    {
      quickBooksItemId,
    }
  );

  return data;
}

/**
 * Create a NEW QBO item through Blume and immediately
 * map it to this BMP Product.
 *
 * Do not send:
 * - SKU
 * - name
 * - description
 * - BULK/PALLET
 *
 * Those values are all resolved server-side from the BMP
 * Product row.
 *
 * The QBO SKU returned after creation becomes authoritative.
 */
export async function createAndMapBlumeProduct(
  productId: string,

  input: CreateAndMapBlumeProductInput = {}
): Promise<CreateAndMapBlumeProductResponse> {
  const { data } = await api.post<CreateAndMapBlumeProductResponse>(
    `/integrations/blume/products/${encodeURIComponent(productId)}/create-and-map`,
    input
  );

  return data;
}

/**
 * Remove only the local BMP -> QBO mapping.
 *
 * This does NOT delete/deactivate the QuickBooks item.
 * Product.sku is also intentionally preserved.
 */
export async function unmapBlumeProduct(productId: string): Promise<UnmapBlumeProductResponse> {
  const { data } = await api.delete<UnmapBlumeProductResponse>(
    `/integrations/blume/products/${encodeURIComponent(productId)}/mapping`
  );

  return data;
}

/*
 * ============================================================
 * DISCONNECT
 * ============================================================
 */

export type DisconnectBlumeResponse = {
  success: boolean;

  disconnected: boolean;

  disconnectedAt: string;
};

export async function disconnectBlume(): Promise<DisconnectBlumeResponse> {
  const { data } = await api.post<DisconnectBlumeResponse>('/integrations/blume/disconnect');

  return data;
}

export type BlumeInvoiceServiceOption = {
  quickBooksItemId: string;
  name: string;
  fullyQualifiedName: string | null;
  description: string | null;
  type: string;
  unitPrice: number | null;
  taxable: boolean | null;
  active: boolean;
};

export type BlumeInvoiceServiceMapping = {
  id: string;
  kind: 'FUEL_SURCHARGE' | 'HIGHWAY_USE_FEE' | 'DIRECT_SHIP_FEE' | 'DELIVERY_FEE';
  quickBooksItemId: string;
  quickBooksItemName: string | null;
  quickBooksItemFullyQualifiedName: string | null;
  quickBooksItemType: string | null;
  defaultUnitPriceCents: number | null;
  createdAt: string;
  updatedAt: string;
};

export type BlumeInvoiceSettingsResponse = {
  settings: {
    fuelSurcharge: BlumeInvoiceServiceMapping | null;
    highwayUseFee: BlumeInvoiceServiceMapping | null;
    directShipFee: BlumeInvoiceServiceMapping | null;
    deliveryFee: BlumeInvoiceServiceMapping | null;
  };

  serviceOptions: BlumeInvoiceServiceOption[];
};

export type SaveBlumeInvoiceSettingsInput = {
  fuelSurcharge: {
    quickBooksItemId: string | null;
    defaultUnitPriceCents: number | null;
  };

  highwayUseFee: {
    quickBooksItemId: string | null;
    defaultUnitPriceCents: number | null;
  };
  directShipFee: {
    quickBooksItemId: string | null;
    defaultUnitPriceCents: null;
  };
  deliveryFee?: { quickBooksItemId: string | null; defaultUnitPriceCents: null };
};

export async function getBlumeInvoiceSettings(): Promise<BlumeInvoiceSettingsResponse> {
  const { data } = await api.get<BlumeInvoiceSettingsResponse>(
    '/integrations/blume/invoice-settings'
  );

  return data;
}

export async function saveBlumeInvoiceSettings(
  input: SaveBlumeInvoiceSettingsInput
): Promise<BlumeInvoiceSettingsResponse> {
  const { data } = await api.put<BlumeInvoiceSettingsResponse>(
    '/integrations/blume/invoice-settings',
    input
  );

  return data;
}
